/**
 * VariantChangeUpdaterSuitShop
 *
 * Factory function that wires up automatic DOM section updates whenever the
 * selected product variant changes. It listens for either a `variantChange`
 * or `sectionsUpdateFinished` event on the main product element, fetches
 * fresh Shopify section HTML via the Sections API, and surgically replaces
 * the relevant sections or blocks in the page without a full reload.
 *
 * Supports two update modes:
 * - **High-variants mode** (`isHighVariants: true`): payload is built from
 *   the currently selected option values (`product-high-variants-suitshop` element).
 * - **Standard mode**: payload is built from the current variant ID.
 *
 * @param {Object}  config               - Configuration object.
 * @param {boolean} config.isHighVariants - When true, uses option values for
 *                                          the request instead of a variant ID.
 * @param {string}  config.productUrl    - The product page URL used as the
 *                                          base for section fetch requests.
 * @returns  Public API with a single `refresh` method.
 */
const VariantChangeUpdaterSuitShop = (config) => {

  const {
    isHighVariants,
    productUrl
  } = config;

  /** @type {number} Monotonic id so stale section loads are ignored without AbortController. */
  let loadRequestId = 0;

  /**
   * Collects all elements marked as updatable on variant change and returns
   * metadata about the Shopify section each one belongs to.
   *
   * Elements are identified by the `.update-on-variant-change` CSS class.
   * Their `data-*` attributes (e.g. `data-type`, `data-parent-section`,
   * `data-parent-block`) are spread onto the returned objects and used later
   * to determine how each section should be updated.
   *
   * @returns {Array<{ section: Element, type: string, parentSection: string, parentBlock?: string }>}
   */
  const getUpdatableSections = () => {
    const sections = Array.from(document.querySelectorAll('[data-variant-dynamic]'))
      .map(updaterElement => ({
        section: updaterElement.closest('.shopify-section'),
        ...updaterElement.dataset
      }))
    return sections
  }

  /**
   * Builds the fetch URL for retrieving updated section HTML from Shopify.
   *
   * Appends either `option_values` or `variant` as a query parameter
   * depending on which is provided, along with the comma-separated list of
   * section IDs to render.
   *
   * @param {Object}  payload            - Request payload.
   * @param {string}  [payload.optionValues] - Comma-separated selected option
   *                                           values (high-variants mode).
   * @param {string|number} [payload.variant] - Variant ID (standard mode).
   * @param {string}  payload.sectionIds  - Comma-separated Shopify section IDs
   *                                        to include in the response.
   * @returns {string} The fully-qualified fetch URL.
   */
  const getLoadUrl = (payload) => {
    const urlObj = new URL(productUrl, window.location.origin);
    if (payload.optionValues) {
      urlObj.searchParams.set('option_values', payload.optionValues);
    } else {
      urlObj.searchParams.set('variant', payload.variant);
    }
    urlObj.searchParams.set('sections', payload.sectionIds);
    const getUrl = urlObj.toString().replaceAll('%2C', ',');
    return getUrl;
  }

  /**
   * Applies a single section's DOM update based on its registered type.
   *
   * - `"section"`: replaces the entire old Shopify section element with the
   *   new one and evals any inline scripts.
   * - `"block"`: finds a specific block element by ID within the section and
   *   replaces only that block, leaving the rest of the section intact.
   *
   * Returns the Promise from `$s.globalEval` when a replacement is made, so
   * that `Promise.all` can track completion. Returns `undefined` if the type
   * is unrecognised or the target block elements cannot be found.
   *
   * @param  sectionData
   *   The old (live) and new (parsed) section elements.
   * @param {Array<{ section: Element, type: string, parentSection: string, parentBlock?: string }>} sections
   *   The full list of updatable section metadata from `getUpdatableSections`.
   * @returns {Promise<void>|undefined}
   */
  const updateDomElement = (sectionData, sections) => {
    const originalSectionData = sections.find(section => section.parentSection === sectionData.oldSection.id.replace(/^shopify-section-/, ''))

    const originalSectionType = originalSectionData ? originalSectionData.type : null;
    if (originalSectionType == 'section') {
      sectionData.oldSection.replaceWith(sectionData.newSection)
      return $s.globalEval(sectionData.newSection)
    } else if (originalSectionType == 'block') {
      const oldBlock = sectionData.oldSection.querySelector(`#block-${originalSectionData.parentBlock}`)
      const newBlock = sectionData.newSection.querySelector(`#block-${originalSectionData.parentBlock}`)
      if (oldBlock && newBlock) {
        oldBlock.replaceWith(newBlock)
        return $s.globalEval(newBlock)
      }
    }
  }

  /**
   * Fetches updated section HTML from the Shopify Section Rendering API and
   * replaces matching sections in the DOM. Older in-flight responses are
   * ignored when a newer variant change starts (request-id guard).
   *
   * @param {Object}  payload                  - Fetch payload forwarded to `getLoadUrl`.
   * @param {string}  [payload.optionValues]    - Selected option values string.
   * @param {string|number} [payload.variant]   - Variant ID.
   * @returns {Promise<void>}
   */
  const loadSections = async (payload) => {
    const sections = getUpdatableSections()
    const sectionIds = sections
      .map(sectionData => sectionData.section.id.replace(/^shopify-section-/, ''))
      .join(',')
    const getUrl = getLoadUrl({ ...payload, sectionIds });
    const requestId = ++loadRequestId;

    try {
      const data = await $s.get({ url: getUrl });
      if (requestId !== loadRequestId) return;

      const jsonData = JSON.parse(data)
      const ignoreAppBlocks = true;
      await Promise.all(
        Object.keys(jsonData)
          .map(key => ({
            oldSection: document.querySelector(`#shopify-section-${key}`),
            newSection: new DOMParser().parseFromString(jsonData[key], 'text/html').querySelector('.shopify-section')
          }))
          .filter(sectionData => sectionData.oldSection != null)
          .filter(sectionData => !(ignoreAppBlocks && sectionData.oldSection.querySelector('.shopify-app-block')))
          .map(sectionData => updateDomElement(sectionData, sections))
      )
    } catch (e) {
      if (requestId !== loadRequestId) return;
      console.error('vcu error', e);
    }
  }

  /**
   * Returns the main product element on the page.
   *
   * @returns {Element|null}
   */
  const getMainElement = () => {
    return document.querySelector('.product-main-element');
  }

  /**
   * Attaches `refresh` as a listener for each of the given event names on
   * the main product element.
   *
   * @param {string[]} listeners - DOM event names to listen for.
   */
  const initListeners = (listeners) => {
    const mainElement = getMainElement();
    if (mainElement) {
      listeners.forEach(listener => {
        mainElement.addEventListener(listener, event => refresh())
      })
    }
  }

  /**
   * Builds the current fetch payload from the live product state and triggers
   * a section reload. Called automatically by the registered event listeners
   * and available publicly for manual invocation.
   */
  const refresh = () => {
    let payload = null;
    if (isHighVariants) {
      const highVariantElements = getMainElement().querySelector('product-high-variants-suitshop')
      payload = { optionValues: highVariantElements.getSelectedValues() }
    } else {
      payload = { variant: getMainElement().getCurrentVariant().id }
    }
    loadSections(payload)
  }

  if (isHighVariants) {
    initListeners(['sectionsUpdateFinished'])
  } else {
    initListeners(['variantChange'])
  }

  return { refresh }
}
